import { atom, selector, selectorFamily } from "recoil";
import { SDKState } from "../../_common/states/SDKStates";
import { getLastOrElse, getOrElse, objectDiff } from "../../../_helpers/_utilities";
import { findIndex, isEmpty, omit, pick } from "lodash";

/**
 * Request to fetch the user data
 */
export const userRequestState = selector({
	key: "userRequest",
	get: async ({get}) => {
		const sdk = get(SDKState);

		if (!sdk) return null;

		// Get all necessary API requests
		const user = await sdk.get("/me");
		if (user.hasOwnProperty("id")) {
			document.getElementById("root").setAttribute("data-uid", user.id);
		}
		return user;
	},
});

export const userState = atom({
	key: "user",
	default: null,
	effects_UNSTABLE: [
		({onSet, trigger, getPromise, node}) => {
			const updateDetails = async () => {
				onSet(async (newValue, oldValue) => {
					if (trigger === "set" || oldValue === null) return;
					const payload = objectDiff(newValue, oldValue);

					// Do not send these properties
					const ignoreProps = ["occupationPreferences", "skillsToSucceedId"];
					const patchToSettingsProps = ["birthYear", "education", "firstName", "gender", "lastName"];

					// Do not send anything in `ignoreProps` in PATCH request, so remove if in payload.
					ignoreProps.forEach((prop) => {
						if (payload.hasOwnProperty(prop)) {
							delete payload[prop];
						}
					});

					// Payload for PATCH /me
					const patchMePayload = omit(payload, patchToSettingsProps);

					// Payload for PATCH /me/settings
					const patchSettingsPayload = pick(payload, patchToSettingsProps);

					// If differences exist, make API request.
					if (!isEmpty(patchMePayload)) {
						const sdk = await getPromise(SDKState);
						sdk.patch("/me", patchMePayload);
					}

					if (!isEmpty(patchSettingsPayload)) {
						const sdk = await getPromise(SDKState);
						const user = await getPromise(node);

						// We have to send a complete payload to this API request, but override anything in the updated atom effect
						const preReqPayload = {
							birthYear: user.birthYear,
							education: user.education,
							firstName: user.firstName,
							gender: user.gender,
							lastName: user.lastName,
						};
						sdk.patch("/me/settings", Object.assign(preReqPayload, patchSettingsPayload));
					}
				});
			};
			updateDetails();
		},
	],
});

export const userOnboardingScreensRequestState = atom({
	key: "userOnboardingScreensRequest",
	default: 0,
});

export const userOnboardingScreensStates = selector({
	key: "userOnboardingScreens",
	get: async ({get}) => {
		const onboardingComplete = get(userOnboardingCompletedState);
		// If no user, just return nothing
		if (onboardingComplete || onboardingComplete === null) {
			return null;
		}
		// If not onboarded, return the appropiate onboarding screen
		const sdk = get(SDKState);
		if (!sdk) {
			return null;
		}
		get(userOnboardingScreensRequestState);
		const response = await sdk.get("/me/onboarding-status");
		return response.screens;
	},
});

export const userOnboardingResumeState = selector({
	key: "userOnboardingResume",
	get: ({get}) => {
		const screens = get(userOnboardingScreensStates);
		if (!screens) {
			return false;
		}
		const incompleteScreens = screens.filter((s) => s.status === "NOT_COMPLETED");
		// This shouldn't be the case, but return nothing if none incomplete
		if (isEmpty(incompleteScreens)) {
			return null;
		}

		return {...incompleteScreens[0], index: findIndex(screens, ["id", incompleteScreens[0].id]) + 1};
	},
});

export const userOnboardingScreenByIndexStates = selectorFamily({
	key: "userOnboardingScreenByIndex",
	get: (index) => ({get}) => {
		const screens = get(userOnboardingScreensStates);
		if (!screens) {
			return null;
		}

		return screens[index - 1];
	},
});

export const userOnboardingScreenByIdIsLastState = selectorFamily({
	key: "userOnboardingScreenByIdIsLast",
	get: (id) => ({get}) => {
		const screens = get(userOnboardingScreensStates);
		if (!screens) {
			return false;
		}
		return getLastOrElse(screens, {id: null}).id === id;
	},
});

export const userCreatedDateState = selector({
	key: "userCreatedDate",
	get: ({get}) => {
		const user = get(userState);
		const oneMonth = (60 * 60 * 24 * 365) / 12;
		const threeMonths = oneMonth * 3;
		const sixMonths = threeMonths * 2;

		const userCreatedAtDate = new Date(user.createdAt * 1000);
		const currentDate = new Date();
		const dateDifference = (currentDate.getTime() - userCreatedAtDate.getTime()) / 1000;

		if (dateDifference < oneMonth) return "less_than_a_month";
		if (dateDifference >= oneMonth && dateDifference < threeMonths) return "1_to_3_month";
		if (dateDifference >= threeMonths && dateDifference < sixMonths) return "3_to_6_month";
		return "more_than_6_month";
	},
});

export const userSubscriptionState = selector({
	key: "userSubscription",
	get: ({get}) => {
		const user = get(userState);
		return user.subscription;
	},
});

export const userPlanState = selector({
	key: "userPlan",
	get: ({get}) => {
		const subscription = get(userSubscriptionState);
		return subscription.plan;
	},
});

export const userFeaturesState = selector({
	key: "userFeatures",
	get: ({get}) => {
		const plan = get(userPlanState);
		return plan.features;
	},
});

export const userHasFeatureState = selectorFamily({
	key: "userHasFeature",
	get: (featureId) => ({get}) => {
		const features = get(userFeaturesState);
		return features.indexOf(featureId) > -1;
	},
});

export const userPremiumState = selector({
	key: "userIsPremium",
	get: ({get}) => {
		const subscription = get(userSubscriptionState);
		return subscription.hasPremiumFeatures;
	},
});

export const userPlatinumState = selector({
	key: "userIsPlatinum",
	get: ({get}) => {
		const subscription = get(userSubscriptionState);
		return subscription.b2CPlatinum;
	},
});

export const userEmploymentState = selector({
	key: "userEmploymentStatus",
	get: ({get}) => {
		const user = get(userState);
		return user.employmentStatus;
	},
});

export const userEmailState = selector({
	key: "userEmail",
	get: ({get}) => {
		const user = get(userState);
		return user.email;
	},
});

export const userFirstNameState = selector({
	key: "userFirstName",
	get: ({get}) => {
		const user = get(userState);
		return user.firstName;
	},
});

export const userLastNameState = selector({
	key: "userLastName",
	get: ({get}) => {
		const user = get(userState);
		return user.lastName;
	},
});

export const userLocationState = selector({
	key: "userLocation",
	get: ({get}) => {
		const user = get(userState);
		return user.location;
	},
});

export const userLocationNameState = selector({
	key: "userLocationName",
	get: ({get}) => {
		const userLoc = get(userLocationState);
		return userLoc ? userLoc.name : null;
	},
});

export const userJobState = selector({
	key: "userJob",
	get: ({get}) => {
		const user = get(userState);
		return user.jobTitle;
	},
});

export const userEducationState = selector({
	key: "userEducation",
	get: ({get}) => {
		const user = get(userState);
		return user.education;
	},
});

export const userBirthYearState = selector({
	key: "userBirthYear",
	get: ({get}) => {
		const user = get(userState);
		return user.birthYear;
	},
});

export const userGenderState = selector({
	key: "userGender",
	get: ({get}) => {
		const user = get(userState);
		return user.gender;
	},
});

export const userAvatarState = selector({
	key: "userAvatar",
	get: ({get}) => {
		const user = get(userState);
		return user.profilePhotoUrl;
	},
});

export const userIsBetaSate = selector({
	key: "userIsBeta",
	get: ({get}) => {
		const user = get(userState);
		return user.betaStatus;
	},
});

export const userSalaryState = selector({
	key: "userSalary",
	get: ({get}) => {
		const user = get(userState);
		return user.minimumSalaryRequirement;
	},
});

export const userOnboardingCompletedState = selector({
	key: "userOnboardingCompleted",
	get: ({get}) => {
		const user = get(userState);
		return getOrElse(() => user.onboardingComplete, null);
	},
});

export const userIsSocialRegisteredState = selector({
	key: "userIsSocialRegistered",
	get: ({get}) => {
		const user = get(userState);
		return ["facebook", "linkedin", "google-oauth2"].indexOf(user.loginProvider) > -1;
	},
});


export const userStrengthsMatchState = selector({
	key: "userStrengthMatch",
	get: ({get}) => {
		const user = get(userState);
		if (!user) return null;
		return getOrElse(() => user.strengths.userMatchingInstance, null);
	}
});


export const userHasAccessToCareerOptionsState = selector({
	key: "userHasAccessToCareerOptions",
	get: ({get}) => get(userHasFeatureState("LABOUR_MARKET_DATA_ACCESS"))
});

export const userHasAccessToJobSearchState = selector({
	key: "userHasAccessToJobSearch",
	get: ({get}) => get(userHasFeatureState("JHUB_ACCESS"))
});

export const userHasAccessToLearningHubState = selector({
	key: "userHasAccessToLearningHub",
	get: ({get}) => get(userHasFeatureState("LHUB_ACCESS"))
});

export const userHasAccessToSkillsLibrary = selector({
	key: "userHasAccessToSkillsLibrary",
	get: ({get}) => get(userHasFeatureState("SKILLS_ACCESS"))
});

export const userHasAccessToCareerHubState = selector({
	key: "userHasAccessToCareerHub",
	get: ({get}) => get(userHasFeatureState("CHUB_ACCESS"))
});

export const userHasAccessToAssessmentsState = selector({
	key: "userHasAccesstoAssessments",
	get: ({get}) => get(userHasFeatureState("STRENGTHS_ARCH_ASSESSMENTS_ACCESS")) || get(userHasAccessToAssessmentPremiumReportsState)
});


export const userHasAccessToAssessmentPremiumReportsState = selector({
	key: "userHasAccesstoAssessmentsReports",
	get: ({get}) => get(userHasFeatureState("STRENGTHS_ARCH_PREMIUM_REPORTS"))
});
